import React, { useState } from 'react';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { PostsService } from '~/services/domain/PostsService';
import { PhotosService } from '~/services/domain/PhotosService';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useTranslation } from 'react-i18next';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Post } from '~/models/Post';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import { ImageUploadButton } from '@/components/ui/image/ImageUploadButton';
import { FeaturedPhotoWarning } from './FeaturedPhotoWarning';
import { PhotoPolicyNotice } from '@/components/domain/common/PhotoPolicyNotice';
import { MembersService } from '@/services/domain/MembersService';
import type { Member } from '~/models/Member';

export interface IAddPostProps extends IContextSheetContentProps {
  payload?: {
    target?: 'feed' | 'wall' | 'event';
    member?: Member;
    onPostCreated?: () => void;
  };
}

export function AddPost({ payload }: IAddPostProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [text, setText] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | File | null>(null);
  const inputRef = useDelayedAutoFocus();

  const handlePostCreated = payload?.onPostCreated;
  const target = payload?.target ?? 'feed';
  const allowImage = target === 'feed' || target === 'event';
  const placeholder =
    target === 'wall' || target === 'event'
      ? t('post.compose.wallPlaceholder', 'Write on {{userName}}’s wall...', {
          userName: payload?.member?.UserName || '',
        })
      : t('post.placeholder');

  const hasContent = text.trim() || selectedImage;
  const canSubmit = target === 'wall' ? !!text.trim() : !!hasContent;
  const isShortPostText = Post.isShortPostText(text);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const trimmedText = text.trim();
    const memberId = payload?.member?.Id;

    setIsSubmitting(true);
    try {
      if (selectedImage && allowImage) {
        await PhotosService.uploadPhoto(selectedImage, {
          eventId: target === 'event' ? memberId : undefined,
          caption: trimmedText || undefined,
        });
      } else if (target === 'wall' || target === 'event') {
        if (!memberId) return;
        await MembersService.createPostOnMemberWall(memberId, trimmedText);
      } else {
        await PostsService.createPost(text);
      }
      showSuccessToast(t('post.createSuccess'));
      handlePostCreated?.();
    } catch (error) {
      console.warn('Failed to create post:', error);
      showErrorToast(t('post.createError'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack className="flex-1 gap-4">
      <Textarea className="flex-1">
        <TextareaInput
          ref={inputRef}
          placeholder={placeholder}
          value={text}
          onChangeText={setText}
          textAlignVertical="top"
          className={`m-0 ${isShortPostText ? 'text-xl' : ''}`}
        />
      </Textarea>

      <HStack className={`shrink-0 ${allowImage ? 'justify-between' : 'justify-end'} items-end`}>
        {allowImage ? (
          <ImageUploadButton
            mode="inline-preview"
            inlinePreviewImage={selectedImage}
            onImageReady={setSelectedImage}
            onImageCancel={() => setSelectedImage(null)}
            isUploading={isSubmitting}
            size="md"
          />
        ) : null}
        <Button
          onPress={handleSubmit}
          disabled={isSubmitting || !canSubmit}
          className="self-end disabled:bg-primary-200"
          action="primary"
          size="md">
          <ButtonText className={isSubmitting || !canSubmit ? 'text-white/50' : ''}>
            {t('post.publish') || 'Publish'}
          </ButtonText>
        </Button>
      </HStack>

      {allowImage && selectedImage ? <PhotoPolicyNotice /> : null}
      {allowImage ? <FeaturedPhotoWarning selectedImage={selectedImage} /> : null}
    </VStack>
  );
}

AddPost.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'md',
  adjustForWebKeyboard: true,
  expectsKeyboard: true,
} satisfies ISheetConfig;
